import React, { createContext, useContext, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useAgentPanel } from './hooks/useAgentPanel'

const AgentPanelStableContext = createContext(null)
const AgentPanelDynamicContext = createContext(null)

export function AgentPanelProvider ({
  agentId,
  workspaceContext,
  onApplyProposal,
  appliedProposalIds,
  children
}) {
  const panel = useAgentPanel(agentId, { workspaceContext })
  const [autoAccept, setAutoAccept] = useState(false)

  const stable = useMemo(() => ({
    addMessage: panel.addMessage,
    cancelThread: panel.cancelThread,
    resumeThread: panel.resumeThread,
    config: panel.config,
    onApplyProposal,
    autoAccept,
    setAutoAccept
  }), [panel.addMessage, panel.cancelThread, panel.resumeThread, panel.config, onApplyProposal, autoAccept])

  const dynamic = useMemo(() => ({
    messages: panel.messages,
    agentStatus: panel.agentStatus,
    isCancelling: panel.isCancelling,
    isPendingAction: panel.isPendingAction,
    isResuming: panel.isResuming,
    interrupts: panel.interrupts,
    processingMessage: panel.processingMessage,
    isThreadLoading: panel.isThreadLoading,
    isErrored: panel.isErrored,
    errorMessage: panel.errorMessage,
    threadId: panel.threadId,
    threadData: panel.threadData,
    appliedProposalIds: appliedProposalIds || new Set()
  }), [panel.messages, panel.agentStatus, panel.isCancelling, panel.isPendingAction, panel.isResuming, panel.interrupts, panel.processingMessage, panel.isThreadLoading, panel.isErrored, panel.errorMessage, panel.threadId, panel.threadData, appliedProposalIds])

  return (
    <AgentPanelStableContext.Provider value={stable}>
      <AgentPanelDynamicContext.Provider value={dynamic}>
        {children}
      </AgentPanelDynamicContext.Provider>
    </AgentPanelStableContext.Provider>
  )
}

AgentPanelProvider.propTypes = {
  agentId: PropTypes.string.isRequired,
  workspaceContext: PropTypes.object,
  onApplyProposal: PropTypes.func,
  appliedProposalIds: PropTypes.instanceOf(Set),
  children: PropTypes.node
}

export function useAgentPanelContext () {
  const stable = useContext(AgentPanelStableContext)
  const dynamic = useContext(AgentPanelDynamicContext)
  if (!stable || !dynamic) {
    throw new Error('useAgentPanelContext must be used within an AgentPanelProvider')
  }
  return { ...stable, ...dynamic }
}
